import React, { useState, useRef, useEffect } from 'react';

/**
 * Generic two-level drilldown dropdown with MULTI-SELECT checkboxes.
 *
 * Props:
 *   parentItems   – ['Bikes', 'Clothing', ...]
 *   childItems    – { Bikes: ['Mountain Bikes', ...], ... }  (optional)
 *   value         – { parents: [], children: {} }
 *                   parents  = array of fully-selected parents
 *                   children = { parent: [child, child, ...] }
 *   onChange       – (newValue) => void
 *   placeholder    – text when nothing selected
 */
const DrilldownSelect = ({
  parentItems = [],
  childItems = null,
  value = { parents: [], children: {} },
  onChange,
  placeholder = 'All',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const ref = useRef(null);

  const parents = value.parents || [];
  const children = value.children || {};
  const hasChildren = childItems && Object.keys(childItems).length > 0;

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // ── Helpers ──

  const isParentFullySelected = (p) => {
    if (parents.includes(String(p))) return true;
    if (hasChildren && childItems[p]) {
      const sel = children[p] || [];
      return sel.length === childItems[p].length;
    }
    return false;
  };

  const isParentPartial = (p) => {
    if (parents.includes(String(p))) return false;
    const sel = children[p] || [];
    return sel.length > 0 && (!childItems[p] || sel.length < childItems[p].length);
  };

  const isChildSelected = (p, c) => {
    if (parents.includes(String(p))) return true;
    return (children[p] || []).includes(c);
  };

  // ── Toggle handlers ──

  const toggleParent = (p) => {
    const key = String(p);
    const next = { parents: [...parents], children: { ...children } };

    if (isParentFullySelected(p)) {
      // Deselect
      next.parents = next.parents.filter((x) => x !== key);
      delete next.children[p];
    } else {
      // Select all
      if (hasChildren && childItems[p]) {
        next.parents = next.parents.filter((x) => x !== key);
        next.children[p] = [...childItems[p]];
      } else {
        if (!next.parents.includes(key)) next.parents.push(key);
      }
    }
    onChange(next);
  };

  const toggleChild = (p, c) => {
    const key = String(p);
    const next = { parents: parents.filter((x) => x !== key), children: { ...children } };
    let sel = [...(children[p] || [])];

    // If parent was fully selected, start from all children
    if (parents.includes(key)) {
      sel = [...childItems[p]];
    }

    if (sel.includes(c)) {
      sel = sel.filter((x) => x !== c);
    } else {
      sel.push(c);
    }

    if (sel.length === 0) {
      delete next.children[p];
    } else {
      next.children[p] = sel;
    }
    onChange(next);
  };

  const toggleExpand = (p) => {
    setExpanded((prev) => ({ ...prev, [p]: !prev[p] }));
  };

  // Clear all
  const clearAll = () => {
    onChange({ parents: [], children: {} });
  };

  // ── Display label ──

  const totalSelected = (() => {
    let count = 0;
    parents.forEach((p) => {
      if (hasChildren && childItems[p]) {
        count += childItems[p].length;
      } else {
        count += 1;
      }
    });
    Object.entries(children).forEach(([p, arr]) => {
      if (!parents.includes(String(p))) count += arr.length;
    });
    return count;
  })();

  let displayLabel = placeholder;
  if (totalSelected === 1) {
    // Show the single selected item name
    if (parents.length === 1 && Object.keys(children).length === 0) {
      displayLabel = parents[0];
    } else {
      const entries = Object.entries(children);
      if (entries.length === 1 && entries[0][1].length === 1) {
        displayLabel = entries[0][1][0];
      }
    }
  } else if (totalSelected > 1) {
    displayLabel = `${totalSelected} selected`;
  }

  return (
    <div className="drilldown-select" ref={ref}>
      <button
        className={`drilldown-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <span className="drilldown-trigger-text">{displayLabel}</span>
        <svg className="drilldown-chevron" width="10" height="6" viewBox="0 0 10 6" fill="none">
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {isOpen && (
        <div className="drilldown-panel">
          {/* Clear / Select All bar */}
          {totalSelected > 0 && (
            <div className="drilldown-bar">
              <button className="drilldown-clear-btn" onClick={clearAll} type="button">
                Clear all
              </button>
            </div>
          )}

          {parentItems.map((p) => (
            <div key={p} className="drilldown-group">
              <div className="drilldown-year-row">
                {hasChildren && childItems[p] ? (
                  <button
                    className={`drilldown-expand-btn ${expanded[p] ? 'expanded' : ''}`}
                    onClick={(e) => { e.stopPropagation(); toggleExpand(p); }}
                    type="button"
                  >
                    <svg width="8" height="8" viewBox="0 0 8 8">
                      <path d="M2 1L6 4L2 7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                ) : (
                  <span style={{ width: 22, flexShrink: 0 }} />
                )}
                <label className="drilldown-check-label">
                  <input
                    type="checkbox"
                    checked={isParentFullySelected(p)}
                    ref={(el) => { if (el) el.indeterminate = isParentPartial(p); }}
                    onChange={() => toggleParent(p)}
                  />
                  <span className="drilldown-checkmark" />
                  <span className="drilldown-check-text">{p}</span>
                </label>
              </div>

              {hasChildren && expanded[p] && childItems[p] && (
                <div className="drilldown-months">
                  {childItems[p].map((c) => (
                    <label key={c} className="drilldown-check-label drilldown-child-item">
                      <input
                        type="checkbox"
                        checked={isChildSelected(p, c)}
                        onChange={() => toggleChild(p, c)}
                      />
                      <span className="drilldown-checkmark" />
                      <span className="drilldown-check-text">{c}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DrilldownSelect;
